import { Fragment, useState } from 'react';

export type Expandable_cell_table_props = {
  headers: string[];
  rows: unknown[][];
  expand_column?: string;
  initial_expanded_rows?: number[];
};

export function column_index(headers: string[], column: string | undefined): number {
  if (!column) {
    return -1;
  }
  return headers.findIndex((header) => header === column);
}

export function is_blank_expand_cell(cell: unknown): boolean {
  if (cell == null) {
    return true;
  }
  return String(cell).trim() === '';
}

export function ExpandSubRow({ text, col_span }: { text: string; col_span: number }) {
  return (
    <tr className="expand-subrow">
      <td colSpan={col_span} className="expand-subrow-cell">
        {text}
      </td>
    </tr>
  );
}

function ExpandToggle({
  expanded,
  column_label,
  on_toggle,
}: {
  expanded: boolean;
  column_label: string;
  on_toggle: () => void;
}) {
  const label = column_label.toLowerCase();
  return (
    <button
      type="button"
      className={`expand-cell-toggle${expanded ? ' is-expanded' : ''}`}
      aria-expanded={expanded}
      aria-label={expanded ? `Hide ${label}` : `Show ${label}`}
      onClick={on_toggle}
    >
      <span className="expand-cell-icon" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <rect x="2.5" y="1.5" width="11" height="13" rx="1.5" stroke="currentColor" />
          <path d="M5 5h6M5 8h6M5 11h4" stroke="currentColor" strokeLinecap="round" />
        </svg>
      </span>
      <span className="expand-cell-chevron" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

export default function ExpandableCellTable({
  headers,
  rows,
  expand_column,
  initial_expanded_rows,
}: Expandable_cell_table_props) {
  const [expanded_rows, set_expanded_rows] = useState<Set<number>>(
    () => new Set(initial_expanded_rows ?? [])
  );
  const expand_index = column_index(headers, expand_column);

  const toggle_row = (row_index: number) => {
    set_expanded_rows((current) => {
      const next = new Set(current);
      if (next.has(row_index)) {
        next.delete(row_index);
      } else {
        next.add(row_index);
      }
      return next;
    });
  };

  return (
    <table className="table">
      <thead>
        <tr>
          {headers.map((header, index) => (
            <th key={index} className={index === expand_index ? 'expand-cell-heading' : undefined}>
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, row_index) => {
          const expand_value = expand_index >= 0 ? row[expand_index] : null;
          const has_expand_text = expand_index >= 0 && !is_blank_expand_cell(expand_value);
          const is_expanded = expanded_rows.has(row_index);

          return (
            <Fragment key={row_index}>
              <tr>
                {row.map((cell, cell_index) => {
                  if (cell_index === expand_index) {
                    return (
                      <td key={cell_index} className="expand-cell">
                        {has_expand_text ? (
                          <ExpandToggle
                            expanded={is_expanded}
                            column_label={expand_column || 'details'}
                            on_toggle={() => toggle_row(row_index)}
                          />
                        ) : null}
                      </td>
                    );
                  }
                  return <td key={cell_index}>{String(cell)}</td>;
                })}
              </tr>
              {has_expand_text && is_expanded ? (
                <ExpandSubRow text={String(expand_value)} col_span={headers.length} />
              ) : null}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
